import { useEffect, useRef } from "react";
import { useRouter } from "next/router";
import { v4 as uuidv4 } from "uuid";
import Cookies from "js-cookie";
import { setUserId as firebaseSetUserId } from "firebase/analytics";
import mixpanel from '@/utils/mixpanel';
import { useUserId } from '@/hooks/useUserId';
import { devIdCookieName } from '@/constants';
import { captureClickIds, noAdsCookieName, resolveRoute } from '@/utils/attribution';
import { hashWithSHA256 } from "@/utils/hashWithSHA256";
import { createUser } from '@/utils/tvlUserApis';
import { analytics } from "@/utils/firebase";

export const TvlApiHandler = () => {
	const { userId, setUserId } = useUserId();
	const router = useRouter();
	// Creating a user is not idempotent, and this effect can re-run before the id lands.
	const userCreationStarted = useRef(false);

	useEffect(() => {
		if (!router.isReady) return;

		if (router.query.noa === "1") {
			Cookies.set(noAdsCookieName, "1");
		}

		// Attribution runs for new and returning users alike: a returning user arriving
		// from a fresh paid click must be re-attributed, so this sits above the identity
		// short-circuit below.
		const route = resolveRoute(router.query);
		captureClickIds(router.query);

		if (userId) {
			firebaseSetUserId(analytics, userId.toString());
			mixpanel.identify(userId.toString());

			if (typeof window.ttq?.identify === "function") {
				hashWithSHA256(userId.toString())
					.then(hashedId => {
						window.ttq!.identify!({ external_id: hashedId });
					});
			};
			return;
		}

		if (userCreationStarted.current) return;
		userCreationStarted.current = true;

		let deviceId = Cookies.get(devIdCookieName);

		if (!deviceId) {
			const oldUserId = Cookies.get('UDCuserID')?.split('_').pop();
			if (oldUserId && /^[0-9a-f]+$/.test(oldUserId)) {
				Cookies.set(devIdCookieName, oldUserId);
				deviceId = oldUserId;

			} else {
				const newId = uuidv4().replace(/-/g, '');
				Cookies.set(devIdCookieName, newId);
				deviceId = newId;
			}
		}

		createUser({
			device_id: deviceId,
			acquired_route: route,
			pseudo_id: Cookies.get("_ga"),
		})
			.then((userId) => {
				setUserId(userId);
			})
			.catch((error) => {
				console.error("Failed to create user:", error);
				userCreationStarted.current = false;
			});

		// `router.asPath` rather than `router.query`: the query object is a fresh identity on
		// every router update, which would re-run this effect in a loop. `setUserId` is a
		// stable zustand setter.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [router.isReady, router.asPath, userId]);

	return null;
};
